import React, { useEffect, useMemo, useState } from "react";
import OtpInput from "./OtpInput";
import styles from "./VerifyEmailOtp.module.scss";
import { LoadingItem } from "../loading";

const RESEND_COUNTDOWN_SECONDS = 60;

interface VerifyEmailOtpProps {
  email: string;
  onVerify: (payload: { email: string; code: string }) => Promise<any>;
  onSuccess: (code: string) => void;
  onResend: () => Promise<any>;
  onLogin?: () => void;
  title?: string;
  description?: string;
  introText?: string;
  verifyButtonLabel?: string;
  loginPromptText?: string;
  loginActionText?: string;
  otpLength?: number;
}

const VerifyEmailOtp: React.FC<VerifyEmailOtpProps> = ({
  email,
  onVerify,
  onSuccess,
  onResend,
  onLogin,
  title = "Verify email address",
  description = "Enter the one time password we sent to your email address.",
  introText,
  verifyButtonLabel = "Verify OTP",
  loginPromptText = "Already have a seller account?",
  loginActionText = "Login",
  otpLength = 4,
}) => {
  const [otpValue, setOtpValue] = useState<string[]>(Array(otpLength).fill(""));
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resendCountdown, setResendCountdown] = useState(
    RESEND_COUNTDOWN_SECONDS,
  );
  const [canResend, setCanResend] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    if (resendCountdown <= 0) {
      setCanResend(true);
      return;
    }

    const timer = window.setTimeout(() => {
      setResendCountdown((previous) => previous - 1);
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [resendCountdown]);

  useEffect(() => {
    if (isVerified) return;

    setOtpValue(Array(otpLength).fill(""));
    setResendCountdown(RESEND_COUNTDOWN_SECONDS);
    setCanResend(false);
    setResendMessage("");
    setSuccessMessage("");
    setHasError(false);
    setErrorMessage("");
  }, [otpLength, email, isVerified]);

  const otpCode = useMemo(() => otpValue.join(""), [otpValue]);
  const isOtpComplete = otpCode.length === otpLength;

  const resetOtpState = () => {
    setOtpValue(Array(otpLength).fill(""));
    setHasError(false);
    setErrorMessage("");
    setSuccessMessage("");
    setIsVerified(false);
  };

  const handleVerify = async () => {
    if (!isOtpComplete || isLoading) return;

    setIsLoading(true);
    setHasError(false);
    setErrorMessage("");
    setResendMessage("");
    setSuccessMessage("");
    setIsVerified(false);

    const response = await onVerify({
      email,
      code: otpCode,
    });

    if (response?.status === "success") {
      setIsLoading(false);
      setIsVerified(true);
      setSuccessMessage("Email verified successfully. Please log in.");
      onSuccess(otpCode);
      return;
    } else {
      setHasError(true);
      setErrorMessage(
        response?.error ||
          "Invalid or expired validation code. Try again or request a new code.",
      );
      setOtpValue(Array(otpLength).fill(""));
    }

    setIsLoading(false);
  };

  const handleResend = async () => {
    if (!canResend || isLoading) return;

    setIsLoading(true);
    setCanResend(false);
    setResendCountdown(RESEND_COUNTDOWN_SECONDS);
    resetOtpState();
    setResendMessage("");
    setSuccessMessage("");

    const response = await onResend();

    if (response?.status === "success") {
      setResendMessage("A new OTP code has been sent to your email.");
    } else {
      setCanResend(true);
      setResendCountdown(0);
      setHasError(true);
      setErrorMessage(
        response?.error ||
          "Invalid or expired validation code. Try again or request a new code.",
      );
    }

    setIsLoading(false);
  };

  return (
    <div className={styles.verify_wrap}>
      {isLoading && (
        <div className={styles.loading_overlay} aria-live="polite">
          <LoadingItem />
        </div>
      )}

      <h2 className={styles.verify_heading}>{title}</h2>
      <p className={styles.verify_subtext}>{description}</p>
      <div className={styles.dividerLine} />

      <div className={styles.otp_section}>
        <p className={styles.otp_intro}>
          {introText ?? `Enter the ${otpLength} digit OTP sent to your email.`}
        </p>
        <OtpInput
          length={otpLength}
          value={otpValue}
          onChange={(value) => {
            setOtpValue(value);
            if (hasError) {
              setHasError(false);
              setErrorMessage("");
            }
            if (resendMessage) {
              setResendMessage("");
            }
          }}
          hasError={hasError}
          disabled={isLoading || isVerified}
          autoFocus
        />

        {hasError && errorMessage && (
          <p className={styles.otp_error_msg} role="alert">
            {errorMessage}
          </p>
        )}

        {!hasError && resendMessage && (
          <p className={styles.otp_success_msg}>{resendMessage}</p>
        )}

        {!hasError && successMessage && (
          <p className={styles.otp_success_msg} role="status">
            {successMessage}
          </p>
        )}
      </div>
      <div className={styles.resend_wrap}>
        {canResend ? (
          <button
            className={styles.resend_btn}
            onClick={handleResend}
            disabled={isLoading}
            type="button"
          >
            Resend OTP
          </button>
        ) : (
          <p className={styles.resend_countdown}>
            Resend available in 00:{String(resendCountdown).padStart(2, "0")}
          </p>
        )}
      </div>
      <button
        className={styles.verify_btn}
        onClick={handleVerify}
        disabled={!isOtpComplete || isLoading || isVerified}
        type="button"
      >
        {isLoading ? "Verifying..." : verifyButtonLabel}
      </button>

      <div className={styles.footerDivider} />

      <div className={styles.auth_footer}>
        <span className={styles.icon}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 10 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.55807 9.00831C7.19141 9.11664 6.75807 9.16665 6.24974 9.16665H3.74974C3.24141 9.16665 2.80807 9.11664 2.44141 9.00831C2.53307 7.92498 3.64557 7.0708 4.99974 7.0708C6.35391 7.0708 7.46641 7.92498 7.55807 9.00831Z"
              stroke="#4B4B4B"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6.2487 0.833252H3.7487C1.66536 0.833252 0.832031 1.66659 0.832031 3.74992V6.24992C0.832031 7.82492 1.30703 8.68742 2.44036 9.00825C2.53203 7.92492 3.64453 7.07074 4.9987 7.07074C6.35286 7.07074 7.46536 7.92492 7.55703 9.00825C8.69036 8.68742 9.16536 7.82492 9.16536 6.24992V3.74992C9.16536 1.66659 8.33203 0.833252 6.2487 0.833252ZM4.9987 5.90408C4.1737 5.90408 3.50703 5.23326 3.50703 4.40826C3.50703 3.58326 4.1737 2.91659 4.9987 2.91659C5.8237 2.91659 6.49037 3.58326 6.49037 4.40826C6.49037 5.23326 5.8237 5.90408 4.9987 5.90408Z"
              stroke="#4B4B4B"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6.49115 4.40842C6.49115 5.23342 5.82448 5.90424 4.99948 5.90424C4.17448 5.90424 3.50781 5.23342 3.50781 4.40842C3.50781 3.58342 4.17448 2.91675 4.99948 2.91675C5.82448 2.91675 6.49115 3.58342 6.49115 4.40842Z"
              stroke="#4B4B4B"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <p>
          {loginPromptText}
          <span className={styles.sign_in} onClick={onLogin}>
            {loginActionText}
          </span>
        </p>
      </div>
    </div>
  );
};

export default VerifyEmailOtp;
